import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { fossaPlanMap } from '../../../../shared/subscriptions/consts';
import { isEnterpriseTier } from '../../../../shared/subscriptions/utils';
import { Action, ResourceType } from '../../../../typings/permissions';
import { window } from '../../../globals';
import { getCookie, setCookie } from '../../../helpers/cookies';
import useHasPermission from '../../../shared/permissions/useHasPermission';
import DismissibleCard from '../../generic/Card/DismissibleCard';
import Link from '../../generic/Link/Link';
import MailToLink from '../../generic/Link/MailToLink';

import { useReleaseGroupLimit } from './hooks/useReleaseGroupLimit';

export const RELEASE_GROUP_BANNER_DISMISSED_COOKIE_NAME = 'dismissedReleaseGroupLimitWarningBanner';
export const RELEASE_GROUP_BANNER_DISMISSED_COOKIE_EXPIRATION_DAYS = 14;

const useHasBannerBeenDismissed = () => {
  const [isDismissed, setIsDismissed] = useState(() => {
    return getCookie(RELEASE_GROUP_BANNER_DISMISSED_COOKIE_NAME) === 'true';
  });

  const onDismissBanner = () => {
    setIsDismissed(() => {
      setCookie(
        RELEASE_GROUP_BANNER_DISMISSED_COOKIE_NAME,
        'true',
        RELEASE_GROUP_BANNER_DISMISSED_COOKIE_EXPIRATION_DAYS,
      );
      return true;
    });
  };

  return {
    isDismissed,
    onDismissBanner,
  };
};

type Props = {
  onDismissBanner: () => void;
};

/**
 * Banner to display when the organization has reached the release group limit.
 */
const ReleaseGroupLimitWarningBanner: VoidFunctionComponent<Props> = ({ onDismissBanner }) => {
  const hasPermission = useHasPermission();
  const {
    value,
    isLoading,
    isAtReleaseGroupLimit,
    isReleaseGroupsUnlimited,
    isOverReleaseGroupLimit,
  } = useReleaseGroupLimit();
  const shouldShowReleaseGroupWarningBanner =
    !isReleaseGroupsUnlimited && (isAtReleaseGroupLimit || isOverReleaseGroupLimit);

  if (!shouldShowReleaseGroupWarningBanner || isLoading) {
    return null;
  }
  const canEditBilling = !window.on_prem && hasPermission({
    action: Action.Edit,
    resourceType: ResourceType.OrganizationBillingSettings,
  });
  const plan = window?.user?.organization?.stripe_plan;
  const planLabel = plan ? fossaPlanMap[plan]?.label ?? '' : '';
  const isPlanEnterprise = plan
    && isEnterpriseTier({ plan, stripePriceLimit: window?.user?.organization?.stripePriceLimit });
  const maxLimit = value?.max || 0;

  if (maxLimit < 1) {
    return null;
  }

  return (
    <DismissibleCard isFullWidth variant="fill" color="pink" onHide={onDismissBanner} padding="medium">
      <DismissibleCard.Header>You have reached the maximum number of release groups.</DismissibleCard.Header>
      <DismissibleCard.Content>
        Your <span className="bold">{planLabel} plan</span> is limited to {maxLimit}{' '}
        {maxLimit === 1 ? 'release group' : 'release groups'}.{' '}
        {canEditBilling && !isPlanEnterprise && (
          <>
            You can increase this limit by <Link to="https://fossa.com/request-demo" openInNewTab>upgrading to an enterprise plan.</Link>
          </>
        )}
        {canEditBilling && isPlanEnterprise && (
          <>
            You can increase this limit by contacting our sales team at{' '}
            <MailToLink email="sales@fossa.com">sales@fossa.com</MailToLink>.
          </>
        )}
      </DismissibleCard.Content>
    </DismissibleCard>
  );
};

/**
 * This container component allows us to skip rendering the banner if the user has dismissed it,
 * or if they don't have permission to edit release groups. This saves us network requests.
 */
const ReleaseGroupLimitWarningBannerContainer = () => {
  const hasPermission = useHasPermission();
  const { isDismissed, onDismissBanner } = useHasBannerBeenDismissed();

  const canEditAnyReleaseGroups = hasPermission({
    resourceType: ResourceType.ReleaseGroupAny,
    action: Action.Edit,
  });

  if (isDismissed || !canEditAnyReleaseGroups) {
    return null;
  }
  return <ReleaseGroupLimitWarningBanner onDismissBanner={onDismissBanner} />;
};

export default ReleaseGroupLimitWarningBannerContainer;
